import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { bookings, tickets, ticketSegments, payments, legsSchedule } from '../../services/api';
import { formatDateTime, formatDate, formatAmount, formatFlightTimeRange } from '../../utils/dateFormat';
import {
  bookingStatuses,
  ticketStatuses,
  segmentStatuses,
  legsScheduleStatuses,
  paymentTypes,
  paymentDirections,
  changeByTypes
} from '../../constants/travelEnums';

// Modal component for showing leg instance change history
function ChangeHistoryModal({ isOpen, onClose, historyData, segment }) {
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Flight Change History</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5em', cursor: 'pointer' }}>&times;</button>
        </div>

        <div style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
          <strong>{segment?.carrier_iata} {segment?.flight_number}</strong> - {segment?.dep_iata} → {segment?.arr_iata}
        </div>

        {historyData.length === 0 ? (
          <p>No change history available.</p>
        ) : (
          <div>
            {(() => {
              // Check if the segment is confirmed (status 10) or replaced/cancelled
              const isSegmentConfirmed = segment?.tsegment_status === 10;
              return historyData.map((item, index) => {
                const isLast = index === historyData.length - 1 && historyData.length > 1;
                // Instance date for the header (date of this leg instance)
                const scheduleDate = item.lsch_date;
                // Change date for the badge tooltip (when this entry was changed)
                const changeDate = item.lsch_change_date;
                const changeTooltip = changeDate ? `Changed on ${formatDate(changeDate)}` : '';
                return (
                  <div key={item.lsch_id} style={{
                    padding: '12px',
                    borderLeft: `3px solid ${item.lsch_status === 10 ? '#3498db' : item.lsch_status === 30 ? '#f39c12' : item.lsch_status === 40 ? '#27ae60' : '#e74c3c'}`,
                    background: index === historyData.length - 1 ? '#e8f4fd' : '#fafafa',
                    marginBottom: '10px',
                    borderRadius: '0 4px 4px 0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                      <div>
                        <strong>
                          {index === 0 ? 'Original' : `Change #${item.lsch_chg_num}`}
                          {scheduleDate && (
                            <span style={{ marginLeft: '8px', color: '#7f8c8d', fontSize: '0.85em', fontWeight: 'normal' }}>
                              ({formatDate(scheduleDate)})
                            </span>
                          )}
                          {isLast && (isSegmentConfirmed ? ' (Current)' : ' (Last)')}
                        </strong>
                      </div>
                      {/* Hide flight status only for the last entry of replaced/cancelled segments */}
                      {(isSegmentConfirmed || !isLast) && (
                        <span
                          className={`badge ${item.lsch_status === 10 ? 'badge-info' : item.lsch_status === 30 ? 'badge-warning' : item.lsch_status === 40 ? 'badge-success' : 'badge-danger'}`}
                          title={changeTooltip}
                          style={{ cursor: changeTooltip ? 'help' : 'default' }}
                        >
                          {legsScheduleStatuses[item.lsch_status]}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.95em', color: '#555' }}>
                      <div><strong>Departure:</strong> {formatDateTime(item.lsch_dep_date, item.lsch_dep_time)}</div>
                      <div><strong>Arrival:</strong> {formatDateTime(item.lsch_arr_date, item.lsch_arr_time)}</div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        <div style={{ marginTop: '15px', textAlign: 'right' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [allTickets, setAllTickets] = useState([]);
  const [allSegments, setAllSegments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [allLegs, setAllLegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [expandedSegmentHistory, setExpandedSegmentHistory] = useState({});
  const [expandedTicketHistory, setExpandedTicketHistory] = useState({});

  useEffect(() => {
    loadBookingDetails();
  }, [id]);

  // Toggle segment replacement history visibility
  const toggleSegmentHistory = (segmentId) => {
    setExpandedSegmentHistory(prev => ({
      ...prev,
      [segmentId]: !prev[segmentId]
    }));
  };

  // Toggle ticket replacement history visibility
  const toggleTicketHistory = (ticketId) => {
    setExpandedTicketHistory(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  // Get replacement history for a segment (previous segments in the chain)
  const getSegmentReplacementHistory = (segment) => {
    if (!segment.tsegment_init_id) return [];
    // Find all segments with the same init_id and lower chg_num
    return allSegments.filter(s =>
      (s.tsegment_init_id === segment.tsegment_init_id || s.tsegment_id === segment.tsegment_init_id) &&
      s.tsegment_id !== segment.tsegment_id &&
      (s.tsegment_chg_num || 0) < (segment.tsegment_chg_num || 0)
    ).sort((a, b) => (b.tsegment_chg_num || 0) - (a.tsegment_chg_num || 0));
  };

  // Get replacement history for a ticket (previous tickets in the chain)
  const getTicketReplacementHistory = (ticket) => {
    if (!ticket.ticket_init_id) return [];
    // Find all tickets with the same init_id and lower chg_num
    return allTickets.filter(t =>
      (t.ticket_init_id === ticket.ticket_init_id || t.ticket_id === ticket.ticket_init_id) &&
      t.ticket_id !== ticket.ticket_id &&
      (t.ticket_chg_num || 0) < (ticket.ticket_chg_num || 0)
    ).sort((a, b) => (b.ticket_chg_num || 0) - (a.ticket_chg_num || 0));
  };

  const loadChangeHistory = async (segment) => {
    if (!segment.tsegment_init_lsch_id || !segment.tsegment_last_lsch_id) return;

    try {
      const response = await legsSchedule.getChain(segment.tsegment_init_lsch_id, segment.tsegment_last_lsch_id);
      setHistoryData(response.data);
      setSelectedSegment(segment);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error('Failed to load change history', err);
    }
  };

  const loadBookingDetails = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [bookingRes, ticketsRes, segmentsRes, paymentsRes, bookingsRes, legsRes] = await Promise.all([
        bookings.getById(id),
        tickets.getAll(),
        ticketSegments.getAll(),
        payments.getAll(),
        bookings.getAll(),
        // load legs to support 'Operated by' display
        (await import('../../services/api')).legs.getAll()
      ]);

      setBooking(bookingRes.data);
      setAllTickets(ticketsRes.data);
      setAllSegments(segmentsRes.data);
      setAllPayments(paymentsRes.data);
      setAllBookings(bookingsRes.data);
      setAllLegs(legsRes.data);

      setError(null);
    } catch (err) {
      setError('Failed to load booking details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get all booking IDs in the hierarchy (this booking + all nested)
  const getAllBookingIds = (rootId) => {
    const ids = [rootId];
    const findNested = (parentId) => {
      const nested = allBookings.filter(b => b.booking_parent_id === parentId);
      nested.forEach(n => {
        ids.push(n.booking_id);
        findNested(n.booking_id);
      });
    };
    findNested(rootId);
    return ids;
  };

  // Get tickets for a specific booking
  const getTicketsForBooking = (bookingId) => {
    return allTickets.filter(t => t.ticket_booking_id === bookingId);
  };

  // Get segments for a specific ticket
  const getSegmentsForTicket = (ticketId) => {
    return allSegments.filter(s => s.tsegment_ticket_id === ticketId);
  };

  // Get 'Operated by' text for a leg id (returns string or null)
  const getOperatedByForLegId = (legId) => {
    if (!legId || allLegs.length === 0) return null;
    const leg = allLegs.find(l => l.leg_id === legId);
    if (!leg) return null;
    if (!leg.leg_operational_leg_id) return null; // not a marketing leg
    const op = allLegs.find(l2 => l2.leg_id === leg.leg_operational_leg_id);
    if (!op) return null;
    return `${op.carrier_iata || op.carrier_name} ${op.flight_number}`;
  };

  // Get payments for a booking hierarchy
  const getPaymentsForBooking = (bookingIds) => {
    return allPayments.filter(
      p => bookingIds.includes(p.payment_booking_id) || bookingIds.includes(p.payment_root_booking_id)
    );
  };

  const getPaymentRecipient = (payment) => {
    if (!payment) return '-';
    if (payment.payment_ctype === 10) {
      return payment.agent_name || `Agent #${payment.payment_agent_id || ''}`.trim();
    }
    if (payment.payment_ctype === 20) {
      return payment.carrier_name || `Carrier #${payment.payment_carrier_id || ''}`.trim();
    }
    if (payment.payment_ctype === 30) {
      return 'Individual';
    }
    return '-';
  };

  // Helpers to display what a payment is applied to
  const getPaymentTargetLabel = (payment) => {
    if (!payment) return '-';
    // Build lines: Booking, Ticket, Segment (each on its own line)
    const lines = [];

    // Specific booking (nested)
    if (payment.payment_booking_id) {
      const b = allBookings.find(bb => bb.booking_id === payment.payment_booking_id);
      lines.push(b ? `Booking: ${b.booking_pnr}` : `Booking: ${payment.payment_booking_id}`);
    }

    // Ticket
    if (payment.payment_ticket_id) {
      const t = allTickets.find(tt => tt.ticket_id === payment.payment_ticket_id);
      lines.push(t ? `Ticket: ${t.ticket_number || `Ticket ${t.ticket_id}`}` : `Ticket: ${payment.payment_ticket_id}`);
    }

    // Segment (show as dep -> arr)
    if (payment.payment_tsegment_id) {
      const seg = allSegments.find(s => s.tsegment_id === payment.payment_tsegment_id);
      if (seg) {
        const dep = seg.dep_iata || seg.dep_airport || seg.dep_iata;
        const arr = seg.arr_iata || seg.arr_airport || seg.arr_iata;
        lines.push(`Segment: ${dep} → ${arr}`);
      } else {
        lines.push(`Segment: ${payment.payment_tsegment_id}`);
      }
    }

    if (lines.length === 0) return '-';
    return (
      <div>
        {lines.map((ln, idx) => (
          <div key={idx} style={{ fontSize: '0.9em', color: '#333', display: 'flex', alignItems: 'center' }}>
            <span style={{ display: 'inline-block', width: '75px', textAlign: 'right', marginRight: '5px', fontWeight: 'bold' }}>
              {ln.split(':')[0]}:
            </span>
            <span style={{ textAlign: 'left' }}>
              {ln.split(':').slice(1).join(':').trim()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const paymentSubjects = {
    10: 'Tickets',
    20: 'Add-ons'
  };

  const paymentSubjectSubtypes = {
    21: 'Refundability',
    22: 'Changeability',
    23: 'Seat selection',
    24: 'Meal',
    25: 'Insurance'
  };

  // Get nested bookings for a parent
  const getNestedBookings = (parentId) => {
    return allBookings.filter(b => b.booking_parent_id === parentId);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2>Booking Details</h2>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div>
        <div className="page-header">
          <h2>Booking Details</h2>
        </div>
        <div className="alert alert-danger">{error || 'Booking not found'}</div>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            const filter = location.state?.filter || 'current';
            navigate('/my-bookings', { state: { filter } });
          }}
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  const getStatusBadge = (status, type, changeDate, changeBy) => {
    const statuses = type === 'booking' ? bookingStatuses : type === 'ticket' ? ticketStatuses : segmentStatuses;
    const colors = { 10: 'badge-success', 20: 'badge-warning', 30: 'badge-danger' };

    let tooltip = '';
    const changeByText = changeBy ? changeByTypes[changeBy] : null;
    if (status === 20 && changeDate) {
      tooltip = `Replaced on ${formatDate(changeDate)}${changeByText ? ` by ${changeByText}` : ''}`;
    } else if (status === 30 && changeDate) {
      tooltip = `Cancelled on ${formatDate(changeDate)}${changeByText ? ` by ${changeByText}` : ''}`;
    }

    return (
      <span
        className={`badge ${colors[status]}`}
        title={tooltip}
        style={{ cursor: tooltip ? 'help' : 'default' }}
      >
        {statuses[status]}
      </span>
    );
  };

  // Render a single booking's content (tickets, segments)
  const renderBookingContent = (bookingData, isNested = false) => {
    const bookingTickets = getTicketsForBooking(bookingData.booking_id);
    const nestedBookings = getNestedBookings(bookingData.booking_id);

    return (
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: isNested ? '2px solid #3498db' : 'none'
      }}>
        {/* Booking Info */}
        <div style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <h3 style={{ margin: 0 }}>
              {isNested ? `Nested Booking ${bookingData.booking_pnr}` : 'Booking Information'}
            </h3>
            {getStatusBadge(bookingData.booking_status, 'booking', bookingData.booking_change_date, bookingData.booking_change_by)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '0.95em' }}>
            {!isNested && <div><strong>PNR:</strong> {bookingData.booking_pnr}</div>}
            <div><strong>Date:</strong> {formatDate(bookingData.booking_date)}</div>
            {bookingData.agent_name && <div><strong>Agent:</strong> {bookingData.agent_name}</div>}
            {bookingData.carrier_name && <div><strong>Carrier:</strong> {bookingData.carrier_name}</div>}
          </div>
        </div>

        {/* Tickets for this booking */}
        {bookingTickets.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1em', color: '#2c3e50' }}>
              Tickets ({bookingTickets.length})
            </h4>
            {/* Show Confirmed (10) and Cancelled (30) tickets, skip Replaced (20) - they're in history */}
            {bookingTickets
              .filter(t => t.ticket_status === 10 || t.ticket_status === 30)
              .map((ticket) => {
                const ticketSegments = getSegmentsForTicket(ticket.ticket_id);
                const ticketReplacementHistory = getTicketReplacementHistory(ticket);
                const hasTicketHistory = ticketReplacementHistory.length > 0;
                const isTicketHistoryExpanded = expandedTicketHistory[ticket.ticket_id];

                return (
                  <div key={ticket.ticket_id} style={{
                    border: '1px solid #e0e0e0',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '10px',
                    background: 'white'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        {ticket.ticket_number && (
                          <div><strong>Ticket #{ticket.ticket_number}</strong></div>
                        )}
                        {ticket.ticket_date && (
                          <div><strong>Date of issue:</strong> {formatDate(ticket.ticket_date)}</div>
                        )}
                        <div><strong>Passenger:</strong> {ticket.passenger_name}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getStatusBadge(ticket.ticket_status, 'ticket', ticket.ticket_change_date, ticket.ticket_change_by)}
                        {hasTicketHistory && (
                          <span
                            onClick={() => toggleTicketHistory(ticket.ticket_id)}
                            style={{
                              cursor: 'pointer',
                              color: '#7f8c8d',
                              userSelect: 'none',
                              fontSize: '0.9em'
                            }}
                            title="Show/hide ticket history"
                          >
                            {isTicketHistoryExpanded ? '▼' : '▶'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Segments - show Confirmed (10) and Cancelled (30), skip Replaced (20) - they're in history */}
                    {ticketSegments.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        {ticketSegments
                          .filter(s => s.tsegment_status === 10 || s.tsegment_status === 30)
                          .map((segment) => {
                            // Check if there were changes (init_lsch_id differs from last_lsch_id)
                            const hasFlightChanges = segment.tsegment_init_lsch_id &&
                              segment.tsegment_last_lsch_id &&
                              segment.tsegment_init_lsch_id !== segment.tsegment_last_lsch_id;

                            // Get segment replacement history
                            const replacementHistory = getSegmentReplacementHistory(segment);
                            const hasReplacementHistory = replacementHistory.length > 0;
                            const isHistoryExpanded = expandedSegmentHistory[segment.tsegment_id];

                            return (
                              <div key={segment.tsegment_id} style={{ marginTop: '5px' }}>
                                <div style={{
                                  background: '#f0f4f8',
                                  padding: '8px 10px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <div>
                                    <strong>{segment.carrier_iata || segment.carrier_name} {segment.flight_number}</strong>
                                    <span style={{ marginLeft: '10px' }}>
                                      {segment.dep_iata} → {segment.arr_iata}
                                    </span>
                                    <span style={{ fontSize: '0.85em', color: '#7f8c8d', marginLeft: '10px' }}>
                                      <strong>{formatFlightTimeRange(segment.lsch_dep_date, segment.lsch_dep_time, segment.lsch_arr_date, segment.lsch_arr_time)}</strong>
                                      {hasFlightChanges && (
                                        <span
                                          style={{
                                            marginLeft: '10px',
                                            color: '#e67e22',
                                            cursor: 'pointer',
                                            textDecoration: 'underline'
                                          }}
                                          onClick={() => loadChangeHistory(segment)}
                                        >
                                          (Flight changes)
                                        </span>
                                      )}
                                    </span>
                                    {getOperatedByForLegId(segment.tsegment_leg_id) && (
                                      <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px' }}>
                                        Operated as {getOperatedByForLegId(segment.tsegment_leg_id)}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {getStatusBadge(segment.tsegment_status, 'segment', segment.tsegment_change_date, segment.tsegment_change_by)}
                                    {hasReplacementHistory ? (
                                      <span
                                        onClick={() => toggleSegmentHistory(segment.tsegment_id)}
                                        style={{
                                          cursor: 'pointer',
                                          color: '#7f8c8d',
                                          userSelect: 'none',
                                          fontSize: '0.9em',
                                          width: '1em',
                                          textAlign: 'center'
                                        }}
                                        title="Show/hide segment history"
                                      >
                                        {isHistoryExpanded ? '▼' : '▶'}
                                      </span>
                                    ) : (
                                      <span style={{ width: '1em' }}></span>
                                    )}
                                  </div>
                                </div>

                                {/* Replacement history (collapsed by default) */}
                                {hasReplacementHistory && isHistoryExpanded && (
                                  <div style={{ marginLeft: '20px', borderLeft: '2px solid #ddd', paddingLeft: '10px' }}>
                                    {replacementHistory.map((oldSegment) => {
                                      const hasFlightChanges = oldSegment.tsegment_init_lsch_id &&
                                        oldSegment.tsegment_last_lsch_id &&
                                        oldSegment.tsegment_init_lsch_id !== oldSegment.tsegment_last_lsch_id;
                                      return (
                                      <div key={oldSegment.tsegment_id} style={{
                                        background: '#fafafa',
                                        padding: '6px 10px',
                                        borderRadius: '4px',
                                        marginTop: '4px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        opacity: 0.8,
                                        fontSize: '0.9em'
                                      }}>
                                        <div>
                                          <strong>{oldSegment.carrier_iata || oldSegment.carrier_name} {oldSegment.flight_number}</strong>
                                          <span style={{ marginLeft: '10px' }}>
                                            {oldSegment.dep_iata} → {oldSegment.arr_iata}
                                          </span>
                                          <span style={{ fontSize: '0.85em', color: '#7f8c8d', marginLeft: '10px' }}>
                                            <strong>{formatFlightTimeRange(oldSegment.lsch_dep_date, oldSegment.lsch_dep_time, oldSegment.lsch_arr_date, oldSegment.lsch_arr_time)}</strong>
                                          </span>
                                          {hasFlightChanges && (
                                            <span
                                              style={{
                                                marginLeft: '10px',
                                                color: '#e67e22',
                                                cursor: 'pointer',
                                                fontSize: '0.85em',
                                                textDecoration: 'underline'
                                              }}
                                              onClick={() => loadChangeHistory(oldSegment)}
                                            >
                                              (Flight changes)
                                            </span>
                                          )}
                                          {getOperatedByForLegId(oldSegment.tsegment_leg_id) && (
                                            <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '2px' }}>
                                              Operated as {getOperatedByForLegId(oldSegment.tsegment_leg_id)}
                                            </div>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          {getStatusBadge(oldSegment.tsegment_status, 'segment', oldSegment.tsegment_change_date, oldSegment.tsegment_change_by)}
                                          <span style={{ width: '1em' }}></span>
                                        </div>
                                      </div>
                                    );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}

                    {/* Ticket replacement history (collapsed by default) */}
                    {hasTicketHistory && isTicketHistoryExpanded && (
                      <div style={{ marginTop: '10px', marginLeft: '10px', borderLeft: '2px solid #f39c12', paddingLeft: '10px' }}>
                        {ticketReplacementHistory.map((oldTicket) => {
                          const oldTicketSegments = getSegmentsForTicket(oldTicket.ticket_id);
                          return (
                            <div key={oldTicket.ticket_id} style={{
                              border: '1px solid #e0e0e0',
                              padding: '10px',
                              borderRadius: '6px',
                              marginBottom: '8px',
                              background: '#fff8e1',
                              opacity: 0.9
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                <div style={{ fontSize: '0.95em' }}>
                                  {oldTicket.ticket_number && (
                                    <div><strong>Ticket #{oldTicket.ticket_number}</strong></div>
                                  )}
                                  {oldTicket.ticket_date && (
                                    <div><strong>Date of issue:</strong> {formatDate(oldTicket.ticket_date)}</div>
                                  )}
                                  <div><strong>Passenger:</strong> {oldTicket.passenger_name}</div>
                                </div>
                                {getStatusBadge(oldTicket.ticket_status, 'ticket', oldTicket.ticket_change_date, oldTicket.ticket_change_by)}
                              </div>

                              {/* Segments of old ticket - show all without expansion */}
                              {oldTicketSegments.length > 0 && (
                                <div style={{ marginTop: '6px' }}>
                                  {oldTicketSegments.map((segment) => {
                                    const hasFlightChanges = segment.tsegment_init_lsch_id &&
                                      segment.tsegment_last_lsch_id &&
                                      segment.tsegment_init_lsch_id !== segment.tsegment_last_lsch_id;
                                    return (
                                      <div key={segment.tsegment_id} style={{
                                        background: '#fafafa',
                                        padding: '6px 10px',
                                        borderRadius: '4px',
                                        marginTop: '4px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '0.9em'
                                      }}>
                                        <div>
                                          <strong>{segment.carrier_iata || segment.carrier_name} {segment.flight_number}</strong>
                                          {' '}- {segment.dep_iata} → {segment.arr_iata}
                                          <span style={{ fontSize: '0.85em', color: '#7f8c8d', marginLeft: '10px' }}>
                                            <strong>{formatFlightTimeRange(segment.lsch_dep_date, segment.lsch_dep_time, segment.lsch_arr_date, segment.lsch_arr_time)}</strong>
                                          </span>
                                          {hasFlightChanges && (
                                            <span
                                              style={{
                                                marginLeft: '10px',
                                                color: '#e67e22',
                                                cursor: 'pointer',
                                                fontSize: '0.85em',
                                                textDecoration: 'underline'
                                              }}
                                              onClick={() => loadChangeHistory(segment)}
                                            >
                                              (Flight changes)
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          {getStatusBadge(segment.tsegment_status, 'segment', segment.tsegment_change_date, segment.tsegment_change_by)}
                                          <span style={{ width: '1em' }}></span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {/* Nested bookings (recursive) */}
        {nestedBookings.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1em', color: '#2c3e50' }}>
              Nested Bookings ({nestedBookings.length})
            </h4>
            {nestedBookings.map((nested) => (
              <div key={nested.booking_id}>
                {renderBookingContent(nested, true)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const allBookingIds = getAllBookingIds(parseInt(id));
  const bookingPayments = getPaymentsForBooking(allBookingIds);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Booking Details: {booking.booking_pnr}</h2>
        <button className="btn btn-secondary" onClick={() => navigate('/my-bookings')}>
          Back to My Bookings
        </button>
      </div>

      {/* Main Booking Content (includes nested) */}
      {renderBookingContent(booking)}

      {/* Payments (for entire hierarchy) */}
      {bookingPayments.length > 0 && (() => {
        // Calculate total in base currency (Expense = negative, Income = positive, Discount = ignored)
        const totalBase = bookingPayments.reduce((sum, p) => {
          const amt = Number(p.payment_base_amt) || 0;
          if (p.payment_direction === 10) { // Expense
            return sum - amt;
          } else if (p.payment_direction === 20) { // Income
            return sum + amt;
          }
          return sum; // Discount (30) is ignored
        }, 0);
        const baseCurrCode = bookingPayments[0]?.base_curr_code;
        const baseCurrMinorUnits = bookingPayments[0]?.base_curr_minor_units;

        // Treat very small totals as zero to avoid showing '-0.00' due to floating point errors
        const minorUnits = baseCurrMinorUnits || 2;
        const epsilon = Math.pow(10, -minorUnits) / 2;
        const displayTotal = Math.abs(totalBase) < epsilon ? 0 : totalBase;

        return (
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Payments ({bookingPayments.length})</h3>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>ID</th>
                  <th style={{ textAlign: 'center' }}>Date</th>
                  <th style={{ textAlign: 'center' }}>Related</th>
                  <th style={{ textAlign: 'center' }}>Type</th>
                  <th style={{ textAlign: 'center' }}>Subject</th>
                  <th style={{ textAlign: 'center' }}>Direction</th>
                  <th style={{ textAlign: 'center' }}>Recipient</th>
                  <th style={{ textAlign: 'center' }}>Payment Amount</th>
                  <th style={{ textAlign: 'center' }}>Base Amount</th>
                  <th style={{ textAlign: 'center' }}>Account Amount</th>
                </tr>
              </thead>
              <tbody>
                {bookingPayments.map((payment) => {
                  const isExpense = payment.payment_direction === 10;
                  const sign = isExpense ? '-' : '';
                  return (
                    <tr key={payment.payment_id}>
                      <td style={{ textAlign: 'center' }}>{payment.payment_id}</td>
                      <td style={{ textAlign: 'center' }}>{formatDate(payment.payment_date)}</td>
                      <td style={{ textAlign: 'center' }}>{getPaymentTargetLabel(payment)}</td>
                      <td style={{ textAlign: 'center' }}>{paymentTypes[payment.payment_type]}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.95em' }}>{paymentSubjects[payment.payment_subject_type] || '-'}</div>
                        {payment.payment_subject_subtype && (
                          <div style={{ fontSize: '0.75em', color: '#666' }}>{paymentSubjectSubtypes[payment.payment_subject_subtype] || `Subtype ${payment.payment_subject_subtype}`}</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${
                          payment.payment_direction === 10 ? 'badge-danger' :
                          payment.payment_direction === 20 ? 'badge-success' : 'badge-warning'
                        }`}>
                          {paymentDirections[payment.payment_direction]}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>{getPaymentRecipient(payment)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {sign}{formatAmount(payment.payment_init_amt, payment.init_curr_minor_units)} {payment.init_curr_code}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {sign}{formatAmount(payment.payment_base_amt, payment.base_curr_minor_units)} {payment.base_curr_code}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {payment.payment_acct_amt !== null && payment.acct_curr_code
                          ? `${sign}${formatAmount(payment.payment_acct_amt, payment.acct_curr_minor_units)} ${payment.acct_curr_code}`
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '15px', fontSize: '1.1em', fontWeight: 'bold' }}>
              Total: <span style={{ color: displayTotal >= 0 ? '#1f7a3a' : '#e74c3c' }}>
                {displayTotal >= 0 ? '' : '-'}{formatAmount(Math.abs(displayTotal), minorUnits)} {baseCurrCode}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Change History Modal */}
      <ChangeHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        historyData={historyData}
        segment={selectedSegment}
      />
    </div>
  );
}

export default BookingDetails;
